import { Program } from '@playbook-technologies/common-enums';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, browserHistory } from 'react-router';
import StatusBar from '../StatusBar';
import ajax from '../ajax';
import helpers from '../helpers';
import { CategoryForm } from './CategoryForm';

import { ProgramForm } from './ProgramForm';
import { HeaderPage } from '../../new/pages/layouts';
import { Breadcrumbs } from '@mui/material';

import { SalesType } from '@client/new/common/types';
import { isUnionType } from '../../new/helpers/general';
import { CollectionProgram } from './collections.types';
import { PATHS } from '@client/routes';
import { base64ToString, isBase64 } from '@client/helpers/routes';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { trackEvent } from '@services/tracking';
import * as enums from '@playbook-technologies/common-enums';
import { BackButton } from '@client/new/pages/collection/program';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sprinkles } from '@design-system/sprinkles.css';
import { clsx } from 'clsx';
import { Typography } from '@design-system/components/typography/typography';
import { validateCollection } from './validation';
import { useCustomizations } from '@client/new/hooks/use-customizations';

const getDefaultProgram = (
  influencerId: number,
  salesType: SalesType,
): Partial<CollectionProgram> => ({
  influencerId,
  isVisible: false,
  name: '',
  description: '',
  goal: '',
  color: '6367ef',
  duration: [4, 'weeks'],
  mediaIntro: null,
  mediaCover: null,
  tags: [],
  inAppPurchase: 'allow',
  faq: [],
  publishingBehaviour: Program.PublishingBehaviour.Bottom.value,
  contentStructure: Program.ContentStructure.Sequential.value,
  type: Program.Type.Program.value,
  accessType:
    salesType === 'otp'
      ? Program.AccessType.Exclusive.value
      : Program.AccessType.Premium.value,
});

const getDefaultCategory = (influencerId: number) => ({
  influencerId,
  isVisible: false,
  name: '',
  description: '',
  color: '6367ef',
  mediaIntro: null,
  mediaCover: null,
  tags: [],
  faq: [],
  publishingBehaviour: Program.PublishingBehaviour.Top.value,
  type: Program.Type.Collection.value,
});

const useSaveCollection = (collection, fromPageType?: string) => {
  const [isSaving, setIsSaving] = useState(false);
  const [tree, setTree] = useState({});
  const [message, setMessage] = useState(null);

  const save = useCallback(async () => {
    console.log('[ProgramEdit] Submit called.');
    setIsSaving(true);

    // trim name & description if necessary
    if (collection.name) {
      collection.name = collection.name.trim();
    }
    if (collection.description) {
      collection.description = collection.description.trim();
    }

    const tree = validateCollection(collection, true);
    setTree(tree);
    const isHealthy = helpers.isTreeHealthy(tree);
    const message = isHealthy
      ? 'Passed validation.'
      : 'Required fields are missing or invalid: please check the marked fields and try again.';

    setMessage(message);
    console.log('[ProgramEdit] Generated tree.', tree);
    console.log('[ProgramEdit] Submit called and prepared', collection);

    if (isHealthy) {
      await ajax
        .upsertProgram(collection)
        .then((createdCollection: any) => {
          console.log('Saved Collection?', collection);
          const redirectPath =
            fromPageType && isBase64(fromPageType)
              ? base64ToString(fromPageType)
              : `/admin/partner/${collection.influencerId}/workouts/published/${createdCollection.id}?refresh=true`;

          browserHistory.push(redirectPath);
        })
        .catch((e) => {
          console.log('[CollectionEdit] Error upserting Collection.', e);
          setIsSaving(false);
        });
    } else {
      StatusBar.sendErrorMessage(message);
    }
  }, [collection]);

  return { message, tree, save, isSaving };
};

const isSalesType = isUnionType<SalesType>(['otp', 'subscription']);

type CollectionPath = 'new-program' | 'new-category';

const toBreadcrumbData = (
  path: CollectionPath,
  fromRoute: string,
  influencerId: number,
) => {
  const redirectPath =
    fromRoute && isBase64(fromRoute)
      ? base64ToString(fromRoute)
      : PATHS.PARTNER_WORKOUTS_PUBLISHED(influencerId);
  const breadcrumbParent = redirectPath.startsWith('/subscriptions')
    ? {
        label: 'Subscriptions',
        to: PATHS.SUBSCRIPTIONS(influencerId),
      }
    : redirectPath.startsWith('/otps')
    ? {
        label: 'One-Time Purchases',
        to: PATHS.PURCHASES(influencerId),
      }
    : redirectPath.startsWith('/programs')
    ? {
        label: 'Programs',
        to: PATHS.PROGRAMS(influencerId),
      }
    : path === 'new-program'
    ? {
        label: 'Programs',
        to: PATHS.PROGRAMS(influencerId),
      }
    : {
        label: 'Workouts',
        to: redirectPath,
      };
  const breadcrumbCurrent =
    path === 'new-category' ? 'New Category' : 'New Program';

  return {
    breadcrumbCurrent,
    breadcrumbParent,
  };
};

type SearchParams = Parameters<typeof PATHS.NEW_PROGRAM>[1];

export type ExtProgramType = SalesType | 'category' | 'not-set';

const getExtProgramType = (
  pathname: string,
  searchParams: SearchParams,
): ExtProgramType => {
  if (pathname.includes('/new-category')) return 'category';
  if (!searchParams.salesType) return 'not-set';
  return searchParams.salesType;
};

export type GetViewDataResult = {
  defaultCollection: Partial<CollectionProgram>;
  title: string;
  initialPath: CollectionPath;
};

export const getViewData = (
  influencerId: number,
  type: ExtProgramType,
): GetViewDataResult => {
  switch (type) {
    case 'not-set':
      return {
        title: 'Create program',
        defaultCollection: getDefaultProgram(influencerId, 'subscription'),
        initialPath: 'new-program',
      };
    case 'category':
      return {
        title: 'Create category',
        defaultCollection: getDefaultCategory(influencerId),
        initialPath: 'new-category',
      };
    case 'subscription':
      return {
        title: 'Create Subscription Program',
        defaultCollection: getDefaultProgram(influencerId, 'subscription'),
        initialPath: 'new-program',
      };
    case 'otp':
      return {
        title: 'Create One-Time Purchase',
        defaultCollection: getDefaultProgram(influencerId, 'otp'),
        initialPath: 'new-program',
      };
  }
};

export const NewCollectionPage = (props: any) => {
  const { params } = props;
  const searchParams = props.location.query as SearchParams;
  const fromRoute = searchParams.from;
  const salesType = searchParams.salesType;
  const isSalesTypeSet = isSalesType(salesType);
  const userInfluencerId = useInfluencerId();
  const influencerId = parseInt(params.influencerId);
  const { customizations } = useCustomizations(String(influencerId));
  const isUnifiedProgramsEnabled =
    customizations?.isUnifiedProgramsEnabled === true;
  const type = getExtProgramType(window.location.pathname, searchParams);

  const viewData = getViewData(influencerId, type);
  const defaultCollection =
    isUnifiedProgramsEnabled && type === 'not-set'
      ? getDefaultProgram(influencerId, 'otp')
      : viewData.defaultCollection;
  const { title, initialPath } = viewData;

  const [collection, setCollection] = useState(defaultCollection);
  const [path, setPath] = useState<CollectionPath>(initialPath);

  const collectionForSave = useMemo(() => {
    if (!isUnifiedProgramsEnabled) return collection;
    const { accessType: _strippedAccessType, ...rest } = collection;
    return rest;
  }, [collection, isUnifiedProgramsEnabled]);

  const {
    message,
    tree: validationTree,
    save,
  } = useSaveCollection(collectionForSave, fromRoute);

  useEffect(() => {
    trackEvent({
      type: 'Collection Creation Started',
      payload: {
        'Collection Type':
          collection.type === enums.Program.Type.Program.value
            ? 'Program'
            : 'Category',
        'Product Type':
          collection.accessType === 'exclusive' ? 'otp' : 'subscription',
        Source:
          collection.accessType === 'exclusive'
            ? 'OTP Tab'
            : 'Subscriptions Tab',
      },
    });
  }, []);

  const onTypeChange = useCallback(
    (type) => {
      if (collection) {
        setCollection({
          ...collection,
          type,
          publishingBehaviour:
            type === Program.Type.Program.value
              ? Program.PublishingBehaviour.Bottom.value
              : Program.PublishingBehaviour.Top.value,
        });

        const oldPath: CollectionPath =
          collection.type === Program.Type.Program.value
            ? 'new-program'
            : 'new-category';

        const newPath: CollectionPath =
          type === Program.Type.Program.value ? 'new-program' : 'new-category';

        trackEvent({
          type: 'Collection Type Changed',
          payload: {
            'Collection Type Final': type,
          },
        });

        window.history.replaceState(
          null,
          '',
          window.location.pathname.replace(oldPath, newPath),
        );
        setPath(newPath);
      }
    },
    [collection],
  );

  const onAccessTypeChange = useCallback(
    (accessType) => {
      if (collection) {
        setCollection({
          ...collection,
          accessType,
        } as any);
      }
    },
    [collection],
  );

  if (userInfluencerId && influencerId !== userInfluencerId) return null;

  if (!collection) return null;

  const { breadcrumbParent, breadcrumbCurrent } = toBreadcrumbData(
    path,
    fromRoute,
    influencerId,
  );

  const guideLink =
    collection.accessType === Program.AccessType.Exclusive.value
      ? 'https://help.playbookapp.io/en/collections/9730963-set-your-offering'
      : 'https://help.playbookapp.io/en/articles/9693285-subscription-business-what-a-successful-experience-looks-like';

  return (
    <div>
      <Breadcrumbs className="mx-10">
        <Link to={breadcrumbParent.to} className="text-text800">
          {breadcrumbParent.label}
        </Link>
        <Typography variant="body" className="m-0 text-black">
          {breadcrumbCurrent}
        </Typography>
      </Breadcrumbs>
      <div className="mt-5">
        {collection.type === Program.Type.Collection.value && (
          <div className="bg-white rounded-md p-6">
            <CategoryForm
              title={title}
              collection={collection}
              message={message}
              validationTree={validationTree}
              disabledCollectionType
              onChange={setCollection}
              typeChanged={onTypeChange}
              save={save}
              accessTypeChanged={onAccessTypeChange}
              creationMode
              {...props}
            />
          </div>
        )}

        {collection.type === Program.Type.Program.value && (
          <HeaderPage
            header={
              <div className="flex justify-between items-center w-full mb-8">
                <div className="flex items-center">
                  <div>
                    <BackButton />
                  </div>
                  <Typography
                    variant="headline"
                    size="large"
                    className="pl-4 flex-1"
                  >
                    {title}
                  </Typography>
                </div>
                <div>
                  <a href={guideLink} target="_blank" rel="noreferrer">
                    <Typography
                      variant="body"
                      size="large"
                      className={clsx(
                        'flex gap-2 items-center',
                        sprinkles({ color: 'P-80' }),
                      )}
                    >
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                      Guide
                    </Typography>
                  </a>
                </div>
              </div>
            }
          >
            <div className="bg-white rounded-md p-6">
              <ProgramForm
                creationMode
                collection={collection}
                onChange={setCollection}
                message={message}
                validationTree={validationTree}
                typeChanged={onTypeChange}
                save={save}
                accessTypeChanged={onAccessTypeChange}
                isSellThroughHidden={isSalesTypeSet || isUnifiedProgramsEnabled}
                {...props}
              />
            </div>
          </HeaderPage>
        )}
      </div>
    </div>
  );
};
